import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
// COMMS-LOG-8 (2026-08-28) — owns the Twilio Device and the active call.
// Mounted ABOVE the router on purpose: every admin screen is a conditional
// render, so a call owned by a panel would drop the moment you navigated to
// the deal you were on the phone about.
import { CallProvider } from "@/context/CallContext";
import { ComposeProvider } from "@/context/ComposeContext";
import LoanApplicationForm from "@/pages/LoanApplicationForm";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import MagicLoginPage from "@/pages/MagicLoginPage";  // MAGIC-1
import MyApplications from "@/pages/MyApplications";
import LandingPage from "@/pages/LandingPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ClientPortal from "@/pages/ClientPortal";
import IntroducerPortal from "@/pages/IntroducerPortal";
// Portals-3 (2026-06-21) — Partner Portal for users with role === "partner".
import PartnerPortal from "@/pages/PartnerPortal";
import PortalSettingsPage from "@/pages/PortalSettingsPage";
// Portals-4 (2026-06-21) — public landing page that consumes an
// /portal/accept-invite/:token link from the invite email.
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import { ProtectedRoute, AdminRoute, IntroducerRoute, PartnerRoute } from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
// Impersonation-v1 (2026-06-21) — sticky red banner shown on every page
// when a super-admin has opened a "View as this user" tab. Renders
// nothing when no impersonation token is in localStorage.
import ImpersonationBanner from "@/components/ImpersonationBanner";
import DemoBanner from "@/components/DemoBanner";  // DEMO-1d
// VERCHK-1 (2026-07-12) — polls index.html and toasts "New version available
// — Refresh" when Cloudflare Pages is serving a newer main.<hash>.js than the
// one this tab loaded. Kills the "hard-refresh to see the new panel" ritual.
import useVersionCheck from "@/lib/useVersionCheck";

// PR #101 / R12 — code-split the heavyweight admin bundle and the public
// /sign/:token route. AdminDashboard + ClientDetailPage pull in every
// admin panel (Gmail, Calendar, Templates, Finance, Systems, Lender DB,
// Contacts, Audit Log…) — over half the app's JS. Public users
// hitting /sign/:token shouldn't have to download any of it before they
// can sign. Lazy-loading keeps the login + signing + portal bundles
// small and snappy.
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ClientDetailPage = lazy(() => import("@/pages/ClientDetailPage"));
const SigningPage = lazy(() => import("@/pages/SigningPage"));
// PACKS-1 / W0-15 — the lender-facing pack page. Public, token-gated, and
// lazy like SigningPage: neither is ever reached by a signed-in staff member,
// so neither belongs in the main bundle.
const PackPage = lazy(() => import("@/pages/PackPage"));
const ContinueApplicationPage = lazy(() => import("@/pages/ContinueApplicationPage"));
// User-Profile-Page (2026-06-21) — full per-staff-member profile view.
const UserProfilePage = lazy(() => import("@/pages/UserProfilePage"));
// SOLINA-FULLPAGE-1 (2026-07-22) — claude.ai-style full-screen Solina page
// (history + personal projects rail), Phase 3 of the Solina workspace plan.
const SolinaFullPage = lazy(() => import("@/pages/SolinaFullPage"));
// FP-2 (2026-07-23) — full-screen Finance Proposal builder per deal.
const ProposalBuilderPage = lazy(() => import("@/pages/ProposalBuilderPage"));

// Tiny fallback while a lazy chunk is downloading. Matches the admin-route
// spinner so the transition looks the same as the AdminRoute auth check.
const ChunkSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
    <div className="animate-spin w-8 h-8 border-4 border-[#bd9a59] border-t-transparent rounded-full" />
  </div>
);

function App() {
  // VERCHK-1 — one poller for the whole app (all roles benefit, not just
  // admins; a stale portal bundle is just as confusing to a client).
  useVersionCheck();
  // MOB-14 / C1 (2026-07-14) — register the service worker on load for
  // everyone (was push-enable-only). The worker now runtime-caches static
  // assets + serves an offline fallback, so all roles benefit. Production
  // only: CRA dev server has no sw.js and a dev worker just poisons
  // localhost. Push permission is still requested only from the Enable
  // button (iOS gesture rule) — registering here grants nothing.
  useEffect(() => {
    if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);
  return (
    <ErrorBoundary>
    <AuthProvider>
      {/* ThemeProvider must sit inside AuthProvider so it can gate the
          dark-mode toggle on the active user's role (admin/broker only). */}
      <ThemeProvider>
      {/* CallProvider reads the signed-in user to decide whether to register
          a Twilio Device at all, so it sits inside AuthProvider. */}
      <CallProvider>
      {/* A3 — owns the compose sheet so any number anywhere in the CRM can
          start a text without navigating to Comms first. Inside CallProvider
          for symmetry; the two are independent. */}
      <ComposeProvider>
      <BrowserRouter>
        {/* Impersonation-v1 — mounted OUTSIDE the route Switch so the
            sticky red banner is visible on every page (admin, portal,
            even /login if the impersonator opens a fresh tab while a
            token is already in localStorage). Renders nothing when no
            impersonation token is present. */}
        <ImpersonationBanner />
        {/* DEMO-1d — "Demo · resets nightly" bar; renders nothing off the demo site. */}
        <DemoBanner />
        {/* PR #101 / R12 — Suspense fallback covers all lazy routes below. */}
        <Suspense fallback={<ChunkSpinner />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/magic-login" element={<MagicLoginPage />} />  {/* MAGIC-1 */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/apply" element={<LoanApplicationForm />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/portal" element={<ProtectedRoute allowRoles={["user"]}><ClientPortal /></ProtectedRoute>} />
          <Route path="/portal/applications/:appId" element={<ProtectedRoute allowRoles={["user"]}><ClientPortal /></ProtectedRoute>} />
          {/* Portals-4 — public accept-invite landing. Consumes the token,
              persists the new active entity, then bounces into the portal
              (or /login when the visitor isn't signed in yet). */}
          <Route path="/portal/accept-invite/:token" element={<AcceptInvitePage />} />
          <Route path="/introducer" element={<IntroducerRoute><IntroducerPortal /></IntroducerRoute>} />
          {/* Portals-3 — /partner → /partner/dashboard for the partner role */}
          <Route path="/partner" element={<Navigate to="/partner/dashboard" replace />} />
          <Route path="/partner/dashboard" element={<PartnerRoute><PartnerPortal /></PartnerRoute>} />
          {/* PORTAL-SETTINGS-1 — profile, payout bank (approval-gated) and password */}
          <Route path="/settings" element={<ProtectedRoute allowRoles={["partner", "introducer"]}><PortalSettingsPage /></ProtectedRoute>} />
          <Route path="/sign/:token" element={<SigningPage />} />
          <Route path="/packs/:token" element={<PackPage />} />
          <Route path="/continue/:token" element={<ContinueApplicationPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/my-applications"
            element={
              <ProtectedRoute allowRoles={["user"]}>
                <MyApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/lenders"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/clients/:userId"
            element={
              <AdminRoute>
                <ClientDetailPage />
              </AdminRoute>
            }
          />
          {/* User-Profile-Page — full per-staff profile (deals + downline +
              entities + permissions all on one page). Distinct from
              /admin/clients/:userId which is the deal/application detail. */}
          <Route
            path="/admin/staff/:userId"
            element={
              <AdminRoute>
                <UserProfilePage />
              </AdminRoute>
            }
          />
          {/* SOLINA-FULLPAGE-1 — standalone route, sibling to /admin, same
              break-out pattern as /admin/staff/:userId. */}
          <Route
            path="/solina"
            element={
              <AdminRoute>
                <SolinaFullPage />
              </AdminRoute>
            }
          />
          {/* FP-2 — Finance Proposal builder, break-out page per deal. */}
          <Route
            path="/admin/proposal/:applicationId"
            element={
              <AdminRoute>
                <ProposalBuilderPage />
              </AdminRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </ComposeProvider>
      </CallProvider>
      <Toaster position="top-right" richColors />
      </ThemeProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
